import {
  type RelationEditor,
  useRelationEditor,
  type WithId,
} from '@framework/form/form/relations/useRelationEditor.ts';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { DividerTitleElement } from '@framework/ui/components/text/DividerTitleElement.tsx';
import { type ColumnType } from 'antd/es/table';
import type OLMap from 'ol/Map';
import type VectorSource from 'ol/source/Vector';
import { useEffect, useMemo } from 'react';
import { useFillableSubmissionContext } from '../../../FillableSubmissionContext.tsx';
import {
  MapCadastralAreaItemSchema,
  type MapIntersectionBaseItem,
  MapIntersectionItemSchema,
} from '../createMapSchema.ts';
import {
  type IMapIntersectionLayer,
  isRenderableIntersectionLayer,
} from '../layers/getIntersectionLayersInfo.ts';
import { MapUnitRender } from '../layers/rendering/MapUnitRender.tsx';
import { MapIntersectionsTable } from './MapIntersectionsTable.tsx';

export interface IMapIntersectionLayerSectionProps {
  layer: IMapIntersectionLayer;
  /** Nome del campo form, es. "widgetName@cadastral" o "widgetName@intersections_vincoli" */
  fieldName: string;
  registerEditor: (
    intersectionCode: string,
    editor: RelationEditor<MapIntersectionBaseItem>,
  ) => void;
  mapRef: React.RefObject<OLMap | null>;
  featuresSource: React.RefObject<VectorSource>;
  readonly?: boolean;
  geometryLabelPrefix?: string;
}

/** Configurazione delle colonne fisse catastali. */
const CADASTRAL_FIELDS = [
  {
    key: 'castralMunicipality',
    title: 'Comune (cod. catastale)',
    label: 'Codice catastale',
  },
  {
    key: 'cadastralSection',
    title: 'Sezione catastale',
    label: 'Sezione catastale',
  },
  {
    key: 'cadastralSheet',
    title: 'Foglio catastale',
    label: 'Foglio catastale',
  },
  {
    key: 'cadastralParticle',
    title: 'Particella catastale',
    label: 'Particella catastale',
  },
] as const;

/** Genera le colonne per il caso catastale (SEZIONE/NUMERO/COMUNE/FOGLIO + area particella). */
function buildCadastralColumns(
  fieldName: string,
  indexOfItem: Map<WithId<MapIntersectionBaseItem>, number>,
  readonly?: boolean,
): ColumnType<MapIntersectionBaseItem>[] {
  const textColumns = CADASTRAL_FIELDS.map(({ key, title, label }) => ({
    title,
    dataIndex: key,
    key,
    render: (value: string, record: WithId<MapIntersectionBaseItem>) => {
      // Indice assoluto: l'index di render di antd è relativo alla pagina.
      const rowIndex = indexOfItem.get(record) ?? -1;
      return record.isManual && !readonly ? (
        <FormItem name={`${fieldName}.${rowIndex}.${key}`} label={label}>
          <TextInput
            name={`${fieldName}.${rowIndex}.${key}`}
            readOnly={readonly}
          />
        </FormItem>
      ) : (
        value
      );
    },
  }));

  return [
    ...textColumns,
    {
      title: 'Area particella',
      dataIndex: 'area',
      key: 'area',
      width: '150px',
      render: (value: number) => <MapUnitRender value={value} />,
    },
  ];
}

/** Genera le colonne dinamiche per il caso intersezione custom. */
function buildCustomColumns(
  fieldName: string,
  indexOfItem: Map<WithId<MapIntersectionBaseItem>, number>,
  attributes: string[],
  aliases?: Record<string, string>,
  readonly?: boolean,
): ColumnType<MapIntersectionBaseItem>[] {
  return attributes.map(attr => ({
    title: aliases?.[attr] || attr,
    dataIndex: ['attributes', attr],
    key: attr,
    render: (value: string, record: WithId<MapIntersectionBaseItem>) => {
      // Indice assoluto: l'index di render di antd è relativo alla pagina.
      const rowIndex = indexOfItem.get(record) ?? -1;
      return record.isManual && !readonly ? (
        <FormItem
          name={`${fieldName}.${rowIndex}.attributes.${attr}`}
          label={aliases?.[attr] || attr}
        >
          <TextInput
            name={`${fieldName}.${rowIndex}.attributes.${attr}`}
            readOnly={readonly}
          />
        </FormItem>
      ) : (
        (value ?? '')
      );
    },
  }));
}

/**
 * Sub-component per un singolo layer con intersezione (catastale o custom).
 * Crea il proprio useRelationEditor e lo registra nel parent tramite callback.
 * Genera le colonne appropriate in base a `cadastralPreset` e le passa a MapIntersectionsTable.
 */
export function MapIntersectionLayerSection(
  props: IMapIntersectionLayerSectionProps,
) {
  const {
    layer,
    fieldName,
    registerEditor,
    mapRef,
    featuresSource,
    readonly,
    geometryLabelPrefix,
  } = props;

  const { isBackoffice } = useFillableSubmissionContext();

  const editor = useRelationEditor<MapIntersectionBaseItem>(fieldName);

  useEffect(() => {
    registerEditor(layer.code, editor);
  }, [layer.code, editor, registerEditor]);

  const intersection = layer.intersection;
  const isCadastral = intersection.cadastralPreset === true;

  // Indice assoluto per record (O(1) in cella): l'index di render di antd è
  // relativo alla pagina. Costruita una volta per cambio di `items`.
  const indexOfItem = useMemo(
    () =>
      new Map<WithId<MapIntersectionBaseItem>, number>(
        editor.items.map((item, index) => [item, index]),
      ),
    [editor.items],
  );

  const columns = useMemo(() => {
    return isCadastral
      ? buildCadastralColumns(fieldName, indexOfItem, readonly)
      : buildCustomColumns(
          fieldName,
          indexOfItem,
          intersection.attributes ?? [],
          intersection.aliases,
          readonly,
        );
  }, [fieldName, readonly, isCadastral, intersection, indexOfItem]);

  const defaultItem = useMemo<MapIntersectionBaseItem>(
    () =>
      isCadastral
        ? MapCadastralAreaItemSchema.cast({ isManual: true }, { assert: false })
        : MapIntersectionItemSchema.cast({ isManual: true }, { assert: false }),
    [isCadastral],
  );

  // L'editor è già registrato sopra: qui decidiamo solo se mostrare la tabella
  // (i dati restano comunque salvati nel form e calcolati).
  // - `backofficeOnly`: visibile solo in backoffice.
  // - intersezione custom in profilo basic: nascosta ma calcolata comunque.
  if (intersection.backofficeOnly && !isBackoffice) {
    return null;
  }
  if (!isRenderableIntersectionLayer(layer)) {
    return null;
  }

  return (
    <div>
      <DividerTitleElement title={intersection.title ?? 'Intersezioni'} />
      <MapIntersectionsTable
        columns={columns}
        editor={editor}
        indexOfItem={indexOfItem}
        fieldName={fieldName}
        defaultItem={defaultItem}
        mapRef={mapRef}
        featuresSource={featuresSource}
        readonly={readonly}
        geometryLabelPrefix={geometryLabelPrefix}
      />
    </div>
  );
}
